import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosServices from '../../services/axios';

// --- ASYNC THUNKS ---

/**
 * Fetches all assets for a given group from the backend.
 */
export const fetchAssets = createAsyncThunk(
  'library/fetchAssets',
  async (groupId, { rejectWithValue }) => {
    if (!groupId) {
      console.log("[fetchAssets] No groupId provided, returning empty set.");
      return { assets: [], totalSize: 0 }; 
    }
    try {
      console.log(`[fetchAssets] Fetching assets for groupId: ${groupId}`);
      const response = await axiosServices.get(`/library/assets?groupId=${groupId}`);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to fetch assets';
      console.error(`[fetchAssets] Error for groupId ${groupId}:`, errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

/**
 * Deletes a single asset from the backend.
 */
export const deleteAsset = createAsyncThunk(
  'library/deleteAsset',
  async ({ assetUrl, groupId }, { rejectWithValue, getState }) => {
    try {
      console.log(`[deleteAsset] Deleting single asset: ${assetUrl} from groupId: ${groupId}`);
      const { library } = getState();
      const assetToDelete = library.assets.find(asset => asset.url === assetUrl);
      
      if (!assetToDelete) {
        throw new Error("Asset not found in current state.");
      }

      await axiosServices.post(`/library/delete-asset?groupId=${groupId}`, { url: assetUrl });
      return assetToDelete; // Return the asset object for local state update
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to delete asset';
      console.error(`[deleteAsset] Error for asset ${assetUrl}:`, errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

/**
 * Deletes multiple assets in a single request.
 */
export const deleteMultipleAssets = createAsyncThunk(
  'library/deleteMultipleAssets',
  async ({ urls, groupId }, { rejectWithValue }) => {
    try {
      console.log(`[deleteMultipleAssets] Deleting ${urls.length} assets from groupId: ${groupId}`);
      await axiosServices.post(`/library/delete-multiple-assets?groupId=${groupId}`, { urls });
      return urls; // Return the array of deleted URLs to confirm success
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to delete assets';
      console.error(`[deleteMultipleAssets] Error:`, errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

/**
 * Renames a single asset on the backend.
 */
export const renameAsset = createAsyncThunk(
  'library/renameAsset',
  async ({ oldUrl, newName, groupId }, { rejectWithValue }) => {
    try {
      console.log(`[renameAsset] Renaming ${oldUrl} to ${newName} in groupId: ${groupId}`);
      const response = await axiosServices.post(`/library/rename-asset?groupId=${groupId}`, { oldUrl, newName });
      return { oldUrl, newAsset: response.data.newAsset };
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to rename asset';
      console.error(`[renameAsset] Error renaming ${oldUrl}:`, errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

// --- SLICE DEFINITION ---

const initialState = {
  assets: [],
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  totalSize: 0,
};

const librarySlice = createSlice({
  name: 'library',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Reducers for fetchAssets
      .addCase(fetchAssets.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAssets.fulfilled, (state, action) => {
        state.status = 'succeeded';
        
        // --- THIS IS THE FIX ---
        // Create a Set of existing URLs to check for duplicates efficiently.
        const seenUrls = new Set();
        // Use .filter() to create a new array containing only unique assets.
        const uniqueAssets = action.payload.assets.filter(asset => {
            if (seenUrls.has(asset.url)) {
                return false; // This is a duplicate, so filter it out.
            } else {
                seenUrls.add(asset.url);
                return true; // This is unique, so keep it.
            }
        });
        
        state.assets = uniqueAssets;
        state.totalSize = action.payload.totalSize;
        // --- END OF FIX ---
      })
      .addCase(fetchAssets.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Reducers for deleteAsset (Single)
      .addCase(deleteAsset.fulfilled, (state, action) => {
        const deletedAsset = action.payload;
        state.assets = state.assets.filter((asset) => asset.url !== deletedAsset.url);
        state.totalSize -= (deletedAsset.size || 0);
        if (state.totalSize < 0) state.totalSize = 0;
      })

      // Reducers for deleteMultipleAssets (Bulk)
      .addCase(deleteMultipleAssets.fulfilled, (state, action) => {
        console.log(`[Reducer] Bulk delete of ${action.payload.length} assets fulfilled.`);
      })

      // Reducers for renameAsset
      .addCase(renameAsset.fulfilled, (state, action) => {
          const index = state.assets.findIndex(asset => asset.url === action.payload.oldUrl);
          if (index !== -1) {
              state.assets[index] = action.payload.newAsset;
          }
      });
  },
});

export default librarySlice.reducer;